import React, { useCallback } from 'react';
import {
  ContentType,
  formatMinutes,
  InformationCard,
  InformationCardState,
  useDeviceSize,
  Video,
} from '@atlassian/learning-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';

import { getDuration, shortenDurationString } from 'src/pages/LearningPathPage/utils';
import { useSaveContent } from 'src/hooks/useSaveContent';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';
import { LearningPathLoaderProps } from 'src/pages/LearningPathPage/loader';
import { useCourseProgress } from 'src/store/progress/progressStore';
import { LearningProgressStatus } from 'src/services/http/progress';
import { getCourseUrl, withPrefix } from 'src/utils/getUrl';
import { useStartContent } from 'src/hooks/useStartContent';
import { getSlug } from 'src/utils/slug';
import { getVideoAttrs } from 'src/utils/contentful';
import { OrVideo } from 'src/graphql/generated/graphql';
import { DESKTOP_CONTROLS, FIELD_ID, MOBILE_CONTROLS } from 'src/pages/LearningPathPage/constants';

interface InformationCardWrapperProps {
  urlToShare: string;
}

export const LearningPathInformationCard: React.FC<InformationCardWrapperProps> = ({ urlToShare }) => {
  const { learningPath } = useLoaderData() as LearningPathLoaderProps;
  const { user, isLoading } = useAtlassianUser();
  const { isContentSaved, saveContent } = useSaveContent({
    contentId: learningPath?.sys.id,
    contentType: ContentType.LEARNING_PATH,
    learningPathId: learningPath?.sys.id,
    user,
  });
  const deviceWidth = useDeviceSize();
  const navigate = useNavigate();
  const location = useLocation();
  const [{ progressForCourses, isLoadingProgressForCourses }] = useCourseProgress();
  const videoAttrs = getVideoAttrs(learningPath?.video as unknown as OrVideo);
  const customControls = {
    customMobileControls: MOBILE_CONTROLS,
    customDesktopControls: DESKTOP_CONTROLS,
  };
  const video = videoAttrs && (
    <Video
      name={learningPath.video?.name}
      videoId={learningPath.video?.sys?.id}
      captions={videoAttrs.cdnCaptions || ''}
      poster={videoAttrs.thumbnailUrl}
      controls={true}
      width={learningPath?.video?.videoWidth || 'auto'}
      height={learningPath?.video?.videoHeight || 'auto'}
      orangeUrl={videoAttrs.cdnUrl}
      vimeoUrl={videoAttrs.vimeoUrl}
      youtubeUrl={videoAttrs.youtubeUrl}
      hasResponsiveControls={true}
      customControls={customControls}
    />
  );
  const learningPathSlug = getSlug(learningPath);
  const learningObjectives = learningPath?.learningObjectives?.json
    ? documentToReactComponents(
      learningPath.learningObjectives.json,
      getOptions(learningPath.learningObjectives, deviceWidth),
    )
    : '';

  const learningPathDuration = shortenDurationString(
    formatMinutes(learningPath?.coursesCollection.items.reduce((acc, course) => acc + getDuration(course), 0)),
  );

  const calculateProgress = (): number => {
    if (!progressForCourses || !learningPath) {
      return 0;
    }

    const numberOfCompletedCourses = progressForCourses.courses.filter(
      (p) =>
        learningPath.coursesCollection.items.find((course) => course.sys.id === p.courseId) &&
        p.status === LearningProgressStatus.COMPLETED,
    ).length;

    return Math.round((numberOfCompletedCourses / learningPath.coursesCollection.items.length) * 100);
  };

  const navigateToCourse = useCallback(
    (courseSlug: string) => navigate(withPrefix(getCourseUrl(courseSlug), location.pathname.replace(/\/$/, ''))),
    [navigate, location],
  );

  const startLearningPath = useStartContent({
    items: learningPath?.coursesCollection?.items,
    progress: progressForCourses,
    contentSlug: learningPathSlug,
    contentType: ContentType.LEARNING_PATH,
    userId: user?.account_id,
    navigateToItem: navigateToCourse,
    fieldId: FIELD_ID,
  });

  const getCourseState = (): InformationCardState => {
    if (isLoading || isLoadingProgressForCourses) {
      return InformationCardState.LOADING;
    }

    if (!user || !progressForCourses) {
      return InformationCardState.DEFAULT;
    }

    if (progressForCourses.overallStatus === LearningProgressStatus.IN_PROGRESS) {
      return InformationCardState.IN_PROGRESS;
    }

    if (progressForCourses.overallStatus === LearningProgressStatus.COMPLETED) {
      return InformationCardState.FINISHED;
    }

    return InformationCardState.DEFAULT;
  };

  return (
    <InformationCard
      video={video}
      data-testid="information-card"
      contentType={ContentType.LEARNING_PATH}
      progress={calculateProgress()}
      duration={learningPathDuration}
      description={learningObjectives}
      cardState={getCourseState()}
      isSaved={isContentSaved}
      onSave={user && saveContent}
      urlToShare={urlToShare}
      onClick={startLearningPath}
      title={learningPath?.learningObjectivesTitle}
    />
  );
};
